import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Form,
  Select,
  Upload,
  message,
  Button,
  Modal,
  DatePicker,
  Spin,
} from "antd";
import { useForm } from "antd/es/form/Form";
import DocumentBox from "../../../components/common/DocumentBox";
import { EditFilled, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import API_MANAGER from "../../../API";
import { s3PreUrl } from "../../../constants";
import axios from "axios";

function MedicalAssessmentEdit({
  data,
  medicalAssessmentEdit,
  setMedicalAssessmentEdit,
  medicalAssessmentTypes,
  onUpdate,
}) {
  const [medicalForm] = useForm();
  const [editDocument, setEditDocument] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [documentDeleted, setDocumentDeleted] = useState(false);

  const props = {
    name: "file",
    accept: "image/jpg,image/jpeg,.pdf",
    multiple: false,
    customRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    onChange(info) {
      const { status } = info.file;
      if (status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const handleUpload = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    setDocumentDeleted(false); // Reset delete flag if new file is uploaded
    // Trigger validation when file is uploaded
    if (newFileList.length > 0 && newFileList[0]?.originFileObj) {
      setTimeout(() => {
        medicalForm.validateFields(['document']);
      }, 100);
    }
  };

  const handleDeleteDocument = () => {
    Modal.confirm({
      title: "Delete Document",
      content: "Are you sure you want to delete this document?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        setDocumentDeleted(true);
        setEditDocument(false);
        setFileList([]);
        message.success("Document marked for deletion. Click Update to save changes.");
      },
    });
  };

  const handleFinish = async (values) => {
    console.log("values: ", values);
    setLoading(true);
    try {
      // Validate document upload - it's mandatory
      if (documentDeleted || !data?.document) {
        // If document is deleted or doesn't exist, a new document must be uploaded
        if (!fileList || fileList.length === 0 || !fileList[0]?.originFileObj) {
          message.error("Please upload a document");
          setLoading(false);
          return;
        }
      }

      let documentUrl = data?.document;

      // Handle document deletion
      if (documentDeleted) {
        // If document is deleted, require a new upload
        if (!fileList || fileList.length === 0 || !fileList[0]?.originFileObj) {
          message.error("Please upload a new document");
          setLoading(false);
          return;
        }
      }
      
      // Handle file upload if a new document is selected
      if (fileList?.length > 0 && fileList[0]?.originFileObj) {
        const dataResume = {
          extension: fileList[0]?.type.split("/")[1],
          type: "DOCUMENT",
          name: fileList[0].originFileObj.name,
          contentType: fileList[0]?.type,
        };
        const responseDocument = await API_MANAGER.fileUpload(dataResume);
        await axios.put(
          responseDocument?.data?.data?.url,
          fileList[0]?.originFileObj
        );
        documentUrl = s3PreUrl + responseDocument.data?.data?.key;
      }

      const payload = {
        document: documentUrl,
        medicalAssessment: values.medicalAssessment,
        issueDate: values.issueDate ? dayjs(values.issueDate).format("YYYY-MM-DD") : null,
        userId: data?.userId,
      };

      console.log("Updating medical assessment with payload:", payload);
      await API_MANAGER.updateMedicalAssessment(payload, data._id);
      message.success("Successfully updated medical assessment");
      setLoading(false);
      setMedicalAssessmentEdit(false);
      setDocumentDeleted(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      message.error(error?.response?.data?.message?.message || "Error occurred");
    } finally {
      setLoading(false);
    }
  };

  const setFields = () => {
    medicalForm.setFieldsValue({
      medicalAssessment: data?.medicalAssessment?._id || data?.medicalAssessment?.id,
      issueDate: data?.issueDate ? dayjs(data.issueDate) : null,
    });
  };

  useEffect(() => {
    setFields();
    setDocumentDeleted(false); // Reset delete state when data changes
    setEditDocument(false); // Reset edit state
    setFileList([]); // Reset file list
  }, [data]);

  return (
    <Modal
      centered
      footer={false}
      open={medicalAssessmentEdit}
      title={"Edit Medical Assessment"}
      className="medicalAssessmentModal"
      onCancel={() => {
        setMedicalAssessmentEdit(false);
        setDocumentDeleted(false);
        setEditDocument(false);
        setFileList([]);
      }}
      width={800}
    >
      <Form form={medicalForm} layout="vertical" onFinish={handleFinish}>
        <Spin spinning={loading} />
        <Row className="containner">
          <Col xs={18}>
            {editDocument || documentDeleted || !data?.document ? (
              <Form.Item
                name="document"
                label="Upload Document"
                rules={[
                  {
                    validator: () => {
                      if (documentDeleted || !data?.document) {
                        if (!fileList || fileList.length === 0 || !fileList[0]?.originFileObj) {
                          return Promise.reject(new Error("Please upload a document"));
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                required
                validateTrigger="onSubmit"
              >
                <Upload {...props} onChange={handleUpload}>
                  <Button icon={<UploadOutlined />}>Upload {documentDeleted ? "New" : ""} Document</Button>
                </Upload>
              </Form.Item>
            ) : (
              <DocumentBox
                date={data?.issueDate}
                url={data?.document}
              />
            )}
            {documentDeleted && (
              <div style={{ padding: "10px 0", color: "#ff4d4f", fontSize: "12px" }}>
                <p>Previous document will be deleted. Please upload a new document to continue.</p>
              </div>
            )}
          </Col>
          <Col className="editResume" xs={6}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {!editDocument && !documentDeleted && data?.document && (
                <>
                  <DeleteOutlined 
                    onClick={handleDeleteDocument}
                    style={{ 
                      color: "#ff4d4f", 
                      fontSize: "18px", 
                      cursor: "pointer" 
                    }}
                    title="Delete Document"
                  />
                  <EditFilled 
                    onClick={() => setEditDocument(true)}
                    style={{ fontSize: "18px", cursor: "pointer" }}
                  />
                </>
              )}
            </div>
          </Col>
        </Row>
        <Row gutter={24} style={{marginTop:"20px"}}>
          <Col span={12}>
            <Form.Item
              label="Medical Assessment"
              name="medicalAssessment"
              rules={[
                {
                  required: true,
                  message: "Please select medical assessment type",
                },
              ]}
            >
              <Select placeholder="Select medical assessment type">
                {medicalAssessmentTypes?.map((type, key) => (
                  <Select.Option key={key} value={type.id || type._id}>
                    {type.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Issue Date"
              name="issueDate"
              rules={[
                {
                  required: true,
                  message: "Please select issue date",
                },
              ]}
            >
              <DatePicker
                className="w-100"
                format={"DD-MM-YYYY"}
                placeholder="Select issue date"
                style={{ height: "38px" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={24} className="buttonCol">
            <Button className="btn1" htmlType="submit" loading={loading}>
              Update
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default MedicalAssessmentEdit;

